/**
 * #/login — entrar con el usuario de la federación.
 *
 * Es la única pantalla del portal que le habla al API SIN credencial: manda
 * `POST /api/auth/login` y, si el server acepta, guarda el token opaco de la
 * sesión (revocable) en el store de sesión. A partir de ahí ese token viaja en
 * todos los pedidos y tiene el ROL de la persona, no el poder total del Bearer
 * único de Ajustes.
 *
 * Errores: el API distingue clave mala (401), cuenta bloqueada (423) y freno de
 * fuerza bruta (429), y cada uno se dice con todas las letras. Es deliberado:
 * "no se pudo iniciar sesión" a las 20:55 de un partido no sirve para nada.
 */
import { useState } from 'react';
import { Card } from '../components/States';
import { useT } from '../i18n/use-t';
import type { ApiClient, Identity } from '../lib/api-client';
import { ApiError } from '../lib/api-client';
import { roleLabel, sessionFromLogin, viaLabel, type PortalSession } from '../lib/auth';
import { dateTime } from '../lib/format';
import { navigate } from '../lib/router';

type State =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'error'; message: string };

export function LoginPage({
  client,
  session,
  identity,
  expired = false,
  onLogin,
  onLogout,
}: {
  client: ApiClient;
  session: PortalSession | null;
  /** Identidad confirmada por el API (puede ser de sesión, token o dev). */
  identity: Identity | null;
  /** La sesión se cortó sola por un 401: se dice por qué, no se deja adivinar. */
  expired?: boolean;
  onLogin: (session: PortalSession) => void;
  onLogout: () => void;
}): JSX.Element {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [state, setState] = useState<State>({ kind: 'idle' });
  const t = useT();

  const submit = async (): Promise<void> => {
    if (username.trim() === '' || password === '') {
      setState({ kind: 'error', message: t('auth.missingFields') });
      return;
    }
    setState({ kind: 'sending' });
    try {
      const result = await client.login(username.trim(), password);
      // La contraseña no se guarda ni se deja en el estado un segundo más.
      setPassword('');
      onLogin(sessionFromLogin(result));
      setState({ kind: 'idle' });
      navigate('#/matches');
    } catch (err) {
      // Los `ApiError` ya vienen traducidos (401 de login, 423, 429, red).
      setState({
        kind: 'error',
        message: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const logout = async (): Promise<void> => {
    setState({ kind: 'sending' });
    try {
      // Revoca el token en el server; si no se puede (sin red, token ya
      // vencido), igual se borra la sesión local: quedarse "adentro" en la
      // pantalla sería peor que un token huérfano que el server ya no acepta.
      await client.logout();
    } catch {
      /* no-op */
    } finally {
      onLogout();
      setState({ kind: 'idle' });
    }
  };

  const sending = state.kind === 'sending';

  return (
    <div>
      <div className="page-head">
        <h1>{t('auth.title')}</h1>
      </div>

      {identity !== null ? (
        <Card title={t('auth.title')}>
          <p>
            {t('auth.alreadyIn', {
              name: identity.name,
              role: roleLabel(identity.role, t),
            })}
          </p>
          <p className="hint">
            {viaLabel(identity.via, t)}
            {session !== null && session.since !== ''
              ? ` · ${t('auth.since', { date: dateTime(session.since) })}`
              : ''}
          </p>
          <div className="row">
            <a className="nav-link" href="#/matches">
              {t('auth.goToMatches')}
            </a>
            {session !== null ? (
              <button type="button" disabled={sending} onClick={() => void logout()}>
                {sending ? t('auth.loggingOut') : t('auth.logout')}
              </button>
            ) : null}
          </div>
        </Card>
      ) : null}

      {session === null ? (
        // Sin título: el `h1` de arriba ya dice "Ingresar" y repetirlo sólo
        // agrega ruido (y dos encabezados iguales para un lector de pantalla).
        <Card>
          {expired ? (
            <div className="error-box" role="alert">
              <span>{t('auth.sessionExpired')}</span>
            </div>
          ) : null}
          <p className="hint">{t('auth.intro')}</p>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <label className="field">
              <span>{t('auth.username')}</span>
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={t('auth.usernamePlaceholder')}
                autoComplete="username"
                autoFocus
              />
            </label>

            <label className="field">
              <span>{t('auth.password')}</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
            </label>

            {state.kind === 'error' ? (
              <div className="error-box" role="alert">
                <span>{state.message}</span>
              </div>
            ) : null}

            <div className="row">
              <button type="submit" className="primary" disabled={sending}>
                {sending ? t('auth.submitting') : t('auth.submit')}
              </button>
            </div>
          </form>
        </Card>
      ) : null}

      <Card title={t('auth.compatCard')}>
        <p className="hint">{t('auth.compatHint')}</p>
        <div className="row">
          <a className="nav-link" href="#/settings">
            {t('auth.compatLink')}
          </a>
        </div>
      </Card>
    </div>
  );
}
